import { useState } from "react";
import { Download, FileSpreadsheet, Users, Truck, FileText, Receipt, Package, Calendar, Filter, Car } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportTypes = [
  { id: "customers", name: "Clienti", icon: Users, endpoint: "/export/customers", hasDateFilter: false },
  { id: "suppliers", name: "Fornitori", icon: Truck, endpoint: "/export/suppliers", hasDateFilter: false },
  { id: "vehicles", name: "Veicoli", icon: Car, endpoint: "/export/vehicles", hasDateFilter: false },
  { id: "quotes", name: "Preventivi", icon: FileText, endpoint: "/export/quotes", hasDateFilter: true, hasStatusFilter: true, 
    statuses: [
      { value: "", label: "Tutti" },
      { value: "draft", label: "Bozza" },
      { value: "sent", label: "Inviato" },
      { value: "accepted", label: "Accettato" },
      { value: "rejected", label: "Rifiutato" },
      { value: "invoiced", label: "Fatturato" }
    ]
  },
  { id: "invoices", name: "Fatture", icon: Receipt, endpoint: "/export/invoices", hasDateFilter: true, hasStatusFilter: true,
    statuses: [
      { value: "", label: "Tutti" },
      { value: "issued", label: "Emessa" },
      { value: "paid", label: "Pagata" },
      { value: "cancelled", label: "Annullata" }
    ]
  },
  { id: "supplier-orders", name: "Ordini Fornitori", icon: Package, endpoint: "/export/supplier-orders", hasDateFilter: true, hasStatusFilter: true,
    statuses: [
      { value: "", label: "Tutti" },
      { value: "draft", label: "Bozza" },
      { value: "sent", label: "Inviato" },
      { value: "confirmed", label: "Confermato" },
      { value: "shipped", label: "Spedito" },
      { value: "received", label: "Ricevuto" }
    ]
  },
  { id: "appointments", name: "Appuntamenti", icon: Calendar, endpoint: "/export/appointments", hasDateFilter: true, hasStatusFilter: true,
    statuses: [
      { value: "", label: "Tutti" },
      { value: "scheduled", label: "Programmato" },
      { value: "in_progress", label: "In Corso" },
      { value: "completed", label: "Completato" },
      { value: "cancelled", label: "Annullato" }
    ]
  }
];

export default function Export({ user }) {
  const [selectedType, setSelectedType] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    date_from: "",
    date_to: ""
  });
  const [loading, setLoading] = useState(false);

  const handleExport = async (exportType) => {
    setLoading(true);
    try {
      let url = `${API}${exportType.endpoint}?format=csv`;
      
      if (filters.status) {
        url += `&status=${filters.status}`;
      }
      if (filters.date_from) {
        url += `&date_from=${filters.date_from}`;
      }
      if (filters.date_to) {
        url += `&date_to=${filters.date_to}`;
      }

      const response = await authFetch(url);
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${exportType.id}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${exportType.name} esportati con successo`);
      } else {
        throw new Error("Errore nell'export");
      }
    } catch (error) {
      toast.error("Errore durante l'esportazione");
    } finally {
      setLoading(false);
    }
  };

  const currentExportType = exportTypes.find(e => e.id === selectedType);

  return (
    <div data-testid="export-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Esporta Dati</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Types */}
        <div className="lg:col-span-2">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-6 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
              Seleziona Dati da Esportare
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {exportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`p-4 rounded-sm border transition-all ${
                    selectedType === type.id 
                      ? "bg-primary/10 border-primary text-primary" 
                      : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <type.icon className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm font-medium">{type.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters & Export */}
        <div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 h-full">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-6 flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" />
              Filtri
            </h3>

            {selectedType ? (
              <div className="space-y-4">
                {currentExportType?.hasStatusFilter && (
                  <div className="form-group">
                    <label className="form-label">Stato</label>
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                      {currentExportType.statuses?.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {currentExportType?.hasDateFilter && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Data Da</label>
                      <input
                        type="date"
                        className="form-input"
                        value={filters.date_from}
                        onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Data A</label>
                      <input
                        type="date"
                        className="form-input"
                        value={filters.date_to}
                        onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                      />
                    </div>
                  </>
                )}

                <Button
                  onClick={() => handleExport(currentExportType)}
                  className="w-full btn-primary"
                  disabled={loading}
                >
                  <Download className="w-5 h-5 mr-2" />
                  {loading ? "Esportazione..." : "Scarica CSV"}
                </Button>

                <p className="text-xs text-zinc-500 mt-4">
                  Il file CSV può essere aperto con Excel, Google Sheets o qualsiasi altro programma per fogli di calcolo.
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileSpreadsheet className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">Seleziona un tipo di dato da esportare</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-zinc-900/30 border border-zinc-800 rounded-sm p-4">
        <h4 className="text-sm font-bold text-primary uppercase mb-2">Informazioni Export</h4>
        <ul className="text-sm text-zinc-400 space-y-1">
          <li>• I file CSV sono compatibili con Excel, Google Sheets e LibreOffice Calc</li>
          <li>• I filtri permettono di esportare solo i dati desiderati</li>
          <li>• Per aprire correttamente i caratteri speciali in Excel, importa il file come UTF-8</li>
        </ul>
      </div>
    </div>
  );
}
